"use client";

import SiteAnchor from "@/components/ui/SiteAnchor";

import Image from "next/image";
import { motion } from "motion/react";
import { ArrowRight, Check } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";

import { MembershipCrest } from "@dj/profile-chrome";

import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/navigation";
import { acquisitionSignupHref, reactWebAuthHref } from "@/lib/reactWebAuthUrl";

/** DroneJob.ai brand tokens — Flight Blue / Rotor Orange (brand guidelines). */
const FLIGHT_BLUE = {
  125: "#0A2B47",
  100: "#0F3A5F",
  75: "#1A4F7A",
  50: "#5C82A3",
  25: "#CFDDE9",
  10: "#EEF4F9",
} as const;

const ROTOR_ORANGE = {
  125: "#E2531F",
  100: "#FF6B35",
  75: "#FF8A5E",
  25: "#FFE1D4",
  10: "#FFF3ED",
} as const;

const SOLUTION_GREEN = {
  100: "#15803D",
  25: "#DCFCE7",
  10: "#F0FDF4",
} as const;

const SOLUTION_PURPLE = {
  100: "#7E22CE",
  25: "#F3E8FF",
  10: "#FAF5FF",
} as const;

const GREY = {
  100: "#717182",
  25: "#ECECF0",
  10: "#F6F6F8",
} as const;

type PersonaTone = "flight" | "sky" | "deck" | "rotor";

type PersonaCard = Readonly<{
  id: string;
  tone: PersonaTone;
  imageSrc: string;
  imageAlt: string;
  title: string;
  tagline: string;
  features: readonly string[];
  aiEnables: string;
  aiEnablesLabel?: string;
  cta: string;
  ctaHref: string;
  external?: boolean;
}>;

const toneStyles: Record<
  PersonaTone,
  Readonly<{
    check: string;
  }>
> = {
  flight: {
    check: FLIGHT_BLUE[100],
  },
  sky: {
    check: SOLUTION_GREEN[100],
  },
  deck: {
    check: SOLUTION_PURPLE[100],
  },
  rotor: {
    check: ROTOR_ORANGE[100],
  },
};

const FALLBACK_IMAGES: Record<string, string> = {
  "beginner-pilots": "/home/personas/beginner-pilot-training.jpg",
  pilots: "/home/personas/pilot-quarry-digital-twin-large-pilot.png",
  "drone-businesses": "/home/personas/drone-business-team-planning.png",
  enterprise: "/home/personas/enterprise-drone-program-team.png",
  "agentic-operations": "/home/personas/agentic-mission-control-team.png",
};

const OPTIMIZED_PERSONA_IDS = new Set([
  "pilots",
  "drone-businesses",
  "enterprise",
  "agentic-operations",
]);

const IMAGE_FOCAL_POINTS: Record<string, string> = {
  "beginner-pilots": "44% 44%",
  pilots: "50% 60%",
  "drone-businesses": "50% 0%",
  enterprise: "50% 0%",
  "agentic-operations": "50% 0%",
};

const personaMembershipCrests = {
  "beginner-pilots": "community",
  pilots: "pilot_professional",
  "drone-businesses": "drone_business",
  enterprise: "enterprise",
} as const;

// Membership palettes: readable power copy, soft CTA tints, and dark matching CTA text.
const personaIdentityColors: Record<string, { phrase: string; buttonBg: string; buttonFg: string }> = {
  "beginner-pilots": { phrase: "#99592B", buttonBg: "#F2E5DA", buttonFg: "#79451F" },
  pilots: { phrase: "#8A6705", buttonBg: "#F9EDBE", buttonFg: "#553C00" },
  "drone-businesses": { phrase: "#A3152F", buttonBg: "#F4DDE3", buttonFg: "#8F1930" },
  enterprise: { phrase: "#5C219E", buttonBg: "#EADFF5", buttonFg: "#5C219E" },
  "agentic-operations": { phrase: "#7738B7", buttonBg: "#EADFF3", buttonFg: "#663092" },
};

export default function PersonasSection() {
  const t = useTranslations("Personas");
  const locale = useLocale();
  const signupHref = reactWebAuthHref(locale, "signup");

  const cards = ((t.raw("cards") as PersonaCard[]) ?? []).map((card) => ({
    ...card,
    imageSrc: FALLBACK_IMAGES[card.id] ?? card.imageSrc,
    ctaHref: card.id === "beginner-pilots" ? acquisitionSignupHref(locale, { audience: "aspiring-pilot", task: "profile" }) : card.id === "pilots" ? acquisitionSignupHref(locale, { audience: "professional-pilot", task: "profile" }) : card.ctaHref === "/signup" ? signupHref : card.ctaHref,
    external: card.ctaHref === "/signup",
  }));

  return (
    <section
      className="bg-white pt-6 pb-10 sm:pt-10 sm:pb-12"
      data-component-instance="homePersonasSection"
      style={{ backgroundColor: "#FFFFFF" }}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-10 max-w-4xl text-center sm:mb-14">
          <p
            className="mb-3 landing-text-detail font-bold tracking-[0.14em] uppercase"
            style={{ color: ROTOR_ORANGE[125] }}
          >
            {t("eyebrow")}
          </p>
          <h2
            className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl"
            style={{ color: FLIGHT_BLUE[125] }}
          >
            {t("title")}
          </h2>
          <p
            className="mx-auto max-w-2xl landing-text-body"
            style={{ color: GREY[100] }}
          >
            {t("subtitle")}
          </p>

        </div>

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-6">
          {cards.map((card, index) => {
            const styles = toneStyles[card.tone] ?? toneStyles.flight;
            const identity = personaIdentityColors[card.id] ?? personaIdentityColors["beginner-pilots"];
            const membershipType =
              personaMembershipCrests[
                card.id as keyof typeof personaMembershipCrests
              ];
            return (
              <motion.article
                key={card.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className={`flex h-full flex-col overflow-hidden rounded-2xl border bg-white xl:col-span-2 ${index === 3 ? "xl:col-start-2" : ""}`}
                style={{
                  borderColor: GREY[25],
                  boxShadow: `0 8px 24px rgba(15, 58, 95, 0.12)`,
                }}
              >
                <div
                  className="relative aspect-[16/9] overflow-hidden"
                  style={{ backgroundColor: FLIGHT_BLUE[10] }}
                >
                  <Image
                    src={card.imageSrc}
                    alt={card.imageAlt}
                    fill
                    className="object-cover"
                    style={{
                      objectPosition: IMAGE_FOCAL_POINTS[card.id] ?? "center",
                    }}
                    sizes="(min-width: 1280px) 390px, (min-width: 768px) 50vw, 100vw"
                    unoptimized={!OPTIMIZED_PERSONA_IDS.has(card.id)}
                  />
                  <div
                    className="absolute bottom-4 left-5 z-10 h-14 w-14 drop-shadow-md"
                    data-persona-card-icon={card.id}
                    aria-hidden
                  >
                    {membershipType ? (
                      <MembershipCrest
                        decorative
                        membershipType={membershipType}
                        size="description"
                      />
                    ) : (
                      <Image
                        src="/home/personas/agentic-operations.svg"
                        alt=""
                        width={56}
                        height={56}
                        unoptimized
                      />
                    )}
                  </div>
                </div>

                <div className="relative flex flex-1 flex-col px-5 pb-5 pt-8">
                  <h3
                    className="mb-1 text-xl font-bold"
                    style={{ color: FLIGHT_BLUE[125] }}
                  >
                    {card.title}
                  </h3>
                  <p
                    className="mb-4 mt-3 whitespace-pre-line landing-text-body font-semibold"
                    style={{ color: identity.phrase }}
                  >
                    {card.tagline}
                  </p>
                  <ul className="mb-4 space-y-2">
                    {card.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-start gap-2 landing-text-body"
                        style={{ color: GREY[100] }}
                      >
                        <Check
                          className="mt-0.5 h-4 w-4 shrink-0"
                          style={{ color: styles.check }}
                          aria-hidden
                        />
                        <span style={{ color: FLIGHT_BLUE[125] }}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div
                    className="rounded-xl px-3 py-2.5 landing-text-detail leading-relaxed"
                    style={{
                      backgroundColor: FLIGHT_BLUE[10],
                      color: FLIGHT_BLUE[125],
                    }}
                  >
                    <strong className="font-bold">{card.aiEnablesLabel ?? t("aiEnablesLabel")}</strong>
                    {card.aiEnables.startsWith("—") ? "" : " "}
                    {card.aiEnables}
                  </div>
                  <div className="mt-auto pt-5">
                    <Button
                      asChild
                      className="landing-text-action w-full border-0 shadow-none hover:opacity-90"
                      style={{ backgroundColor: identity.buttonBg, color: identity.buttonFg }}
                    >
                      {card.external ? (
                        <SiteAnchor href={card.ctaHref}>
                          {card.cta}
                          <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
                        </SiteAnchor>
                      ) : (
                        <Link href={card.ctaHref}>
                          {card.cta}
                          <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
                        </Link>
                      )}
                    </Button>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
